import {
  ButtonProps,
  closeCurrentDialog,
  closeDialog,
  KeyboardShortcut,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useState} from 'react';

import {equals, isNil, not} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  BaseDirectSaleLabourItem,
  BulkDeleteDirectSaleItemsRequest,
  GetDirectSaleLabourItemResponse,
  useBulkDeleteDirectSaleItemsMutation,
  useBulkPatchDirectSaleLabourBasketItemMutation,
  useDeleteDirectSaleLabourBasketItemMutation,
  useDeleteDirectSaleLabourBasketItemsMutation,
  useGetDirectSaleLabourBasketItemsQuery,
  useGetDirectSaleQuery,
  useLazyGetDirectSaleLabourItemQuery,
  usePatchDirectSaleLabourBasketItemQuantityMutation,
  usePatchDirectSaleLabourItemMutation,
  usePostDirectSaleLabourBasketCheckoutMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  EitherQuantityOrError,
  FullScreenModal,
  getInvoiceName,
  handleApiError,
  useBasketItemsQuantityState,
} from '@dms/shared';
import {
  AfterSalesLabourBasket,
  LabourBasketItemEditingDetails,
  useAfterSalesHeaderParameters,
  useBasketMechanic,
} from '@dms/shared-aftersales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {focusGridPart} from 'features/eag-grid';

import {useAddLabourItemsToBasket} from './hooks/useAddLabourItemsToBasket';
import {LABOUR_CATALOG_GRID_CODE} from './LABOUR_CATALOG_GRID_CODE';
import {RequestTabLabourBulkEditItem} from './RequestTabLabourBulkEditItem';
import {RequestTabLabourItemEdit} from './RequestTabLabourItemEdit';
import {RequestTabLabourList} from './RequestTabLabourList';

interface AddLabourModalProps extends RequiredTestIdProps {
  directSaleId: string;
  authorizationProfileId: string | Nullish;
  onDiscard: VoidFunction;
}

export function RequestTabLabourModal(props: AddLabourModalProps) {
  const [getDirectSaleLabourItem] = useLazyGetDirectSaleLabourItemQuery();

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetDirectSaleLabourBasketItemsQuery({directSaleId: props.directSaleId});

  const {data: directSale} = useGetDirectSaleQuery({directSaleId: props.directSaleId});

  const headerParameters = useAfterSalesHeaderParameters({
    numberLabel: i18n.t('entity.warehouse.labels.directSale'),
    number: directSale?.number,
    customerLabel: i18n.t('entity.warehouse.labels.customer'),
    customerId: directSale?.customerId,
    vehicleId: directSale?.vehicleId,
  });

  const [checkoutLabourBasket, {isLoading: isCheckingOutBasket}] =
    usePostDirectSaleLabourBasketCheckoutMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] =
    useDeleteDirectSaleLabourBasketItemsMutation();

  const [changeLabourBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchDirectSaleLabourBasketItemQuantityMutation();

  const [bulkDeleteDirectSaleItems, {isLoading: isBulkDeletingLabourItems}] =
    useBulkDeleteDirectSaleItemsMutation();

  const [deleteLabourBasketItem, {isLoading: isDeletingItem}] =
    useDeleteDirectSaleLabourBasketItemMutation();

  const [, {isLoading: isEditingLabourItem}] = usePatchDirectSaleLabourItemMutation({
    fixedCacheKey: 'edit-direct-sale-labour-item',
  });

  const [, {isLoading: isBulkEditingLabourItems}] = useBulkPatchDirectSaleLabourBasketItemMutation({
    fixedCacheKey: 'bulk-edit-direct-sale-labour-item',
  });

  const [focusTrigger, setFocusTrigger] = useState(0);

  const {addItemsToBasket, isAddingItemsToBasket} = useAddLabourItemsToBasket({
    directSaleId: props.directSaleId,
    onItemsAdded: () => setFocusTrigger((prev) => prev + 1),
  });

  const basketItems = basket?.workBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.workBasketTotalPrice;

  const basketMechanicId = basket?.assignMechanicId;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingLabourItem ||
    isBulkEditingLabourItems ||
    isDeletingItem ||
    isBulkDeletingLabourItems;

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const {mechanics, areMechanicsLoading, mechanicPayload, inputMechanicId, handleMechanicChange} =
    useBasketMechanic({
      authorizationProfileId: props.authorizationProfileId,
      assignedMechanicId: basketMechanicId,
    });

  const handleCheckout = async () => {
    if (isNotNil(mechanicPayload)) {
      return await checkoutLabourBasket({
        directSaleId: props.directSaleId,
        body: {
          assignMechanics: mechanicPayload,
        },
      })
        .unwrap()
        .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.labourAdded')))
        .then(props.onDiscard)
        .catch(handleApiError);
    }

    await checkoutLabourBasket({
      directSaleId: props.directSaleId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.labourAdded')))
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleDiscard = async () => {
    await discardBasket({directSaleId: props.directSaleId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeLabourBasketItemQuantity({
      directSaleId: props.directSaleId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = async (editingDetails: LabourBasketItemEditingDetails) => {
    if (isNil(editingDetails.itemId)) {
      return;
    }

    const labourItem = (await getDirectSaleLabourItem({
      itemId: editingDetails.itemId,
      directSaleId: props.directSaleId,
    })
      .unwrap()
      .catch(handleApiError)) as GetDirectSaleLabourItemResponse;

    const itemName = labourItem ? `${getInvoiceName(labourItem)} (${labourItem?.number})` : '';

    openDialog(
      <RequestTabLabourItemEdit
        directSaleId={props.directSaleId}
        item={labourItem}
        isEditingDisabled={false}
        authorizationProfileId={props.authorizationProfileId}
        onClose={closeCurrentDialog}
        data-testid={suffixTestId('dialog.editLabour', props)}
      />,
      {
        title: itemName,
        size: 'large',
        scrollBehavior: 'inside',
        withAdditionalFooter: true,
      }
    );
  };

  const handleBulkDelete = (itemsToDelete: BaseDirectSaleLabourItem[]) => {
    const requestBody: BulkDeleteDirectSaleItemsRequest['body'] = {
      items: itemsToDelete.map((item) => ({id: item.id})),
    };

    openDeleteDialog({
      onConfirm: () =>
        bulkDeleteDirectSaleItems({directSaleId: props.directSaleId, body: requestBody})
          .unwrap()
          .catch(handleApiError),
    });
  };

  const handleDelete = async (itemId: string) => {
    await deleteLabourBasketItem({directSaleId: props.directSaleId, itemId})
      .unwrap()
      .catch(handleApiError);
  };

  const openBulkEditDialog = (itemsToEdit: BaseDirectSaleLabourItem[]) =>
    openDialog(
      <RequestTabLabourBulkEditItem
        itemsToEdit={itemsToEdit}
        directSaleId={props.directSaleId}
        authorizationProfileId={props.authorizationProfileId}
        onClose={() => closeDialog(BULK_EDIT_BASKET_ITEM_DIALOG)}
        data-testid={suffixTestId('bulkEditBasketItem', props)}
      />,
      {
        id: BULK_EDIT_BASKET_ITEM_DIALOG,
        title: i18n.t('entity.warehouse.labels.itemsSettings'),
      }
    );

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      keyboardShortcut: KeyboardShortcut.ModEnter,
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.addWork')}
      parameters={headerParameters}
      actions={actions}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <RequestTabLabourList
              directSaleId={props.directSaleId}
              hasPendingRequest={hasPendingRequest}
              onAddItemsToBasket={addItemsToBasket}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesLabourBasket<BaseDirectSaleLabourItem>
              focusTrigger={focusTrigger}
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{
                mechanics,
                areMechanicsLoading,
                selectedMechanicId: inputMechanicId,
                onMechanicChange: handleMechanicChange,
              }}
              bulkActions={[
                {
                  action: openBulkEditDialog,
                  label: i18n.t('entity.warehouse.labels.itemsSettings'),
                },
                {
                  action: handleBulkDelete,
                  label: i18n.t('general.actions.delete'),
                },
              ]}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTabOut={() => focusGridPart(LABOUR_CATALOG_GRID_CODE, 'smartSearch')}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}

const BULK_EDIT_BASKET_ITEM_DIALOG = 'bulkEditBasketItem';
